import { useRef } from "react";

import { assertNever } from "@impero-com/common/utils/obj";
import { Typography } from "antd";

import {
    MetadataInstanceValue,
    RiskDirectoryNodeResources,
    SavedBlob,
} from "api/generated/model";
import { SimpleFileUploader } from "components/fileUploader/simpleFileUploader";
import { FileUploadResult } from "components/fileUploader/types";
import { BaseFormField } from "components/form/legacyFields";
import { BaseFormDisplay } from "components/form/legacyFields/formDisplay";
import * as CT from "org/risks/content/types";
import { WrappedFormUtils, mapForm, simpleMappers } from "utils/form";
import { assertNonEmpty } from "utils/nonempty";

import * as U from "../content/utils";

import * as T from "./types";

const { Text } = Typography;

export type UpdateRootNodeLinkedResourcesCallback = <
    K extends keyof RiskDirectoryNodeResources,
>(
    callback: (
        prevState: RiskDirectoryNodeResources
    ) => Pick<RiskDirectoryNodeResources, K>
) => void;
type AttachedFilesMetadataFieldProps<ROOT> = T.SharedProperties<
    string,
    ROOT,
    CT.AttachedFilesInstance
> & {
    updateRootNodeLinkedResources: UpdateRootNodeLinkedResourcesCallback;
};

export default function AttachedFilesMetadataField<ROOT>(
    props: AttachedFilesMetadataFieldProps<ROOT>
) {
    const { label, instance } = props;

    const savedBlobs = useRef(instance.value ?? []);

    let error: string | undefined;
    let readOnly: boolean;
    let form: WrappedFormUtils<
        {
            [x: string]: SavedBlob[];
        },
        ROOT
    >;
    switch (props.type) {
        case "READ_ONLY":
            readOnly = true;
            error = undefined;
            break;
        case "CREATE":
            readOnly = false;
            error = undefined;
            form = mapInstanceFormToSavedBlobsForm(props.id, props.form);
            break;
        case "EDIT":
            readOnly = false;
            if (
                props.validationError !== undefined &&
                props.validationError.validateStatus === "error"
            ) {
                error = props.validationError.help;
            }
            break;
        default:
            return assertNever(props);
    }

    type BlobUpdateOperation =
        | { kind: "Add"; blob: SavedBlob }
        | { kind: "Remove"; removedBlobId: string };
    const updateBlobs = (operation: BlobUpdateOperation): void => {
        let newBlobs: SavedBlob[];
        switch (operation.kind) {
            case "Add":
                newBlobs = [...savedBlobs.current, operation.blob];
                break;
            case "Remove":
                newBlobs = savedBlobs.current.filter(
                    (blob) => blob.id !== operation.removedBlobId
                );
                break;
            default:
                return assertNever(operation);
        }
        savedBlobs.current = newBlobs;
        // Now savedBlobs.current contains all the blobs of the component and is never stale.
        //
        // In the following callback we know that the async component may not
        // return a previously updated state if React has not refreshed it yet.
        //
        // To reproduce: upload multiple files at the same time.
        //
        // This is why we recompute the new state from data found in
        // savedBlobs.current which has up-to-date data.
        props.updateRootNodeLinkedResources((prevState) => {
            const prevBlobIds = new Set(prevState.blobs.map((blob) => blob.id));
            const blobsToAdd = savedBlobs.current.filter(
                (blob) => !prevBlobIds.has(blob.id)
            );
            return {
                blobs: [...prevState.blobs, ...blobsToAdd],
            };
        });
    };

    let onFileUploadPromise: (
        promiseFileUpload: Promise<FileUploadResult>
    ) => void;
    switch (props.type) {
        case "EDIT":
            const afterUpload = (
                prevState: MetadataInstanceValue | undefined,
                promisedValue: FileUploadResult
            ): MetadataInstanceValue | null => {
                if (prevState !== undefined && prevState.type !== "BlobIds") {
                    throw new Error("Expected metadata BlobIds for prevState");
                }
                switch (promisedValue.kind) {
                    case "Uploaded":
                        // Set the state of the component
                        updateBlobs({
                            kind: "Add",
                            blob: promisedValue.file.blob,
                        });

                        // Update the node
                        const newValue = [
                            ...(prevState?.value ?? []),
                            promisedValue.file.blob.id,
                        ];
                        return {
                            type: "BlobIds",
                            // Cannot be empty as a file was just uploaded
                            value: assertNonEmpty(newValue),
                        };
                    case "Errored":
                        // Do nothing in case of error.
                        // The FileUloader displays the file as errored
                        throw new Error("File upload has failed");
                    default:
                        return assertNever(promisedValue);
                }
            };
            onFileUploadPromise = (promise: Promise<FileUploadResult>) => {
                props.onChangeAsync(promise, afterUpload);
            };
            break;
        case "CREATE":
            onFileUploadPromise = (promise: Promise<FileUploadResult>) => {
                promise.then((result: FileUploadResult) => {
                    switch (result.kind) {
                        case "Uploaded":
                            updateBlobs({
                                kind: "Add",
                                blob: result.file.blob,
                            });
                            form.setFieldsValue({
                                [props.id]: savedBlobs.current,
                            });
                            break;
                        case "Errored":
                            // Do nothing in case of error.
                            // The FileUloader displays the file as errored
                            break;
                        default:
                            assertNever(result);
                    }
                });
            };
            break;
        case "READ_ONLY":
            onFileUploadPromise = (_promise: Promise<FileUploadResult>) => {
                throw new Error("Can not update in READ_ONLY_NODE");
            };
            break;
        default:
            assertNever(props);
    }

    let onFileDeleted: (removedBlobId: string) => void;
    switch (props.type) {
        case "EDIT":
            onFileDeleted = (removedBlobId: string) => {
                updateBlobs({ kind: "Remove", removedBlobId });
                props.onChangeAsync(Promise.resolve(), (oldValue, _) => {
                    if (oldValue === undefined) {
                        throw new Error(
                            "Cannot remove a file from an already-empty field"
                        );
                    }
                    if (oldValue.type !== "BlobIds") {
                        throw new Error(
                            "Expected BlobIds, got wrong metadata type " +
                                oldValue.type
                        );
                    }
                    return U.extractMetadataInstanceValue({
                        type: "AttachedFilesInstance",
                        fieldType: { type: "AttachedFiles" },
                        value: savedBlobs.current,
                    });
                });
            };
            break;
        case "CREATE":
            onFileDeleted = (removedBlobId: string) => {
                updateBlobs({ kind: "Remove", removedBlobId });
                form.setFieldsValue({
                    [props.id]: savedBlobs.current,
                });
            };
            break;
        case "READ_ONLY":
            onFileDeleted = (_removedBlobId: string) => {
                throw new Error("Can not update in READ_ONLY_NODE");
            };
            break;
        default:
            assertNever(props);
    }

    const getCreateComponent = () => {
        if (props.type !== "CREATE") {
            throw new Error("GetCreateComponent called for non-CREATE type");
        }
        const { getFieldDecorator } = form;
        return getFieldDecorator(props.id, {
            valuePropName: "savedBlobs",
            initialValue: [],
        })(
            <SimpleFileUploader
                deleteAction={{ kind: "DO_NOT_DELETE_BLOB" }}
                readOnly={readOnly}
                onFileUploadPromise={onFileUploadPromise}
                onFileDeleted={onFileDeleted}
            />
        );
    };

    switch (props.type) {
        case "EDIT":
        case "READ_ONLY":
            return (
                <BaseFormDisplay label={label}>
                    <SimpleFileUploader
                        // eslint-disable-next-line react-hooks/refs
                        savedBlobs={savedBlobs.current}
                        deleteAction={{ kind: "DO_NOT_DELETE_BLOB" }}
                        readOnly={readOnly}
                        onFileUploadPromise={onFileUploadPromise}
                        onFileDeleted={onFileDeleted}
                    />
                    {error && <Text type="danger">{error}</Text>}
                </BaseFormDisplay>
            );
        case "CREATE":
            return (
                <BaseFormField
                    getComponent={getCreateComponent}
                    label={label}
                    hasFeedback={false}
                />
            );
        default:
            return assertNever(props);
    }
}

function instanceToSavedBlobs(
    instance: CT.MetadataInstance
): SavedBlob[] | undefined {
    if (instance.type !== "AttachedFilesInstance") {
        throw new Error(
            "Expected AttachedFiles instance, got " + instance.type
        );
    }
    const { value } = instance;
    return value;
}

function savedBlobsToInstance(
    savedBlobs: SavedBlob[] | undefined
): CT.MetadataInstance {
    return {
        type: "AttachedFilesInstance",
        fieldType: { type: "AttachedFiles" },
        value: savedBlobs,
    };
}

function mapInstanceFormToSavedBlobsForm<ROOT>(
    id: string,
    instanceForm: WrappedFormUtils<
        {
            [x: string]: CT.MetadataInstance;
        },
        ROOT
    >
): WrappedFormUtils<
    {
        [x: string]: SavedBlob[];
    },
    ROOT
> {
    const mapper = simpleMappers<
        string,
        CT.MetadataInstance,
        SavedBlob[] | undefined,
        ROOT
    >(id, instanceToSavedBlobs, (value) => savedBlobsToInstance(value));
    return mapForm<Record<string, SavedBlob[]>>()(instanceForm, mapper());
}
